import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV } from "@/lib/data";
import { Logo } from "@/components/Primitives";
import { BookCallButton } from "@/context/BookCall";
import { cn } from "@/lib/utils";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={cn(
                    "fixed top-0 inset-x-0 z-50 border-b transition-all duration-300 px-5 sm:px-8 lg:px-16",
                    scrolled
                        ? "bg-white/95 backdrop-blur border-pa-line py-3"
                        : "bg-white/70 backdrop-blur border-transparent py-5",
                )}
                data-testid="site-header"
            >
                <div className="mx-auto max-w-[1180px] flex items-center justify-between">
                    <Link to="/" aria-label="Phoebus AI home" data-testid="header-logo">
                        <Logo />
                    </Link>

                    <nav className="hidden md:flex items-center gap-8">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.to.replace("/", "")}`}
                                className={({ isActive }) =>
                                    cn(
                                        "text-sm tracking-tight transition-colors hover:text-pa-blue-700",
                                        isActive
                                            ? "text-pa-blue-700"
                                            : "text-pa-ink-900",
                                    )
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden md:block">
                        <BookCallButton className="px-5 py-2.5 text-sm" testId="header-book-call" />
                    </div>

                    <button
                        type="button"
                        className="md:hidden relative z-50 h-10 w-10 flex flex-col items-center justify-center gap-1.5"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        data-testid="mobile-menu-toggle"
                    >
                        <span
                            className={cn(
                                "block h-0.5 w-6 bg-pa-ink-900 transition-transform",
                                open && "translate-y-2 rotate-45",
                            )}
                        />
                        <span
                            className={cn(
                                "block h-0.5 w-6 bg-pa-ink-900 transition-opacity",
                                open && "opacity-0",
                            )}
                        />
                        <span
                            className={cn(
                                "block h-0.5 w-6 bg-pa-ink-900 transition-transform",
                                open && "-translate-y-2 -rotate-45",
                            )}
                        />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-40 bg-pa-ink-900 md:hidden flex flex-col"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        data-testid="mobile-menu"
                    >
                        <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
                            {NAV.map((item, i) => (
                                <motion.div
                                    key={item.to}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06 }}
                                >
                                    <NavLink
                                        to={item.to}
                                        className="font-display text-4xl font-bold text-white py-2 block"
                                    >
                                        {item.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="p-6 border-t border-white/10">
                            <BookCallButton
                                className="w-full justify-center"
                                variant="onDark"
                                testId="mobile-book-call"
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
